import * as React from "react";
import RefineIcon from "components/icons/refine";
import theme from "styles/theme";
import { CollectionKeys } from "constants/product-collection";
import { Map, List } from "immutable";
import { buildViewRecord } from "components/product-collection/product-collection-views/product-collection-linesheet-view/dummy_view_def";
import { parseFilters } from "./helpers";
import { ProductCollectionCatalog } from "components/product-collection/product-collection-side-panel/product-collection-catalog";
import IconButton from "material-ui/IconButton";
import Filters from "components/filters/filters";
import Popover from "material-ui/Popover";
import { HEADER_HEIGHT } from "./shop-tool-panel";
import { Breadcrumbs } from "./breadcrumbs";
import { Location } from "history";
import { RouterState, InjectedRouter } from "react-router";
import type { IScope } from "reducers/scope";
import { IDisplayFields, ISchemaKey } from "helpers/product-collection";
import { ProductCollectionContext } from "components/product-collection/product-collection-context";
import { getRawItemDisplayProperty } from "helpers/collection";

interface IProps {
    children: React.ReactNode;
    breadcrumbs: Array<{ displayName: string; path?: string }>;
    scope: IScope;
    router: RouterState & InjectedRouter;
    onFetchEntries: (catalogId, cacheKey, filters) => void;
    onFetchEntriesNextPage: (catalogId, nextPage, fromAssortment?) => void;
    onFetchCatalog: (id) => void;
    collection: any;
    items?: List<any>;
    location: Location;
    catalog: any;
    app: any;
    entities: any;
    entries: any;
    isViewDefFetching: boolean;
    itemsId: string;
    onAddItem: (params) => void;
    inAssortmentProducts: { [key: string]: string[] };
    collectionEditable: boolean;
    shopViewFields: IDisplayFields;
    brandEntities: any;
}

export const ShopCatalog: React.FunctionComponent<IProps> = ({
    children,
    breadcrumbs,
    scope,
    router,
    onFetchEntries,
    onFetchCatalog,
    items,
    catalog,
    app,
    entities,
    isViewDefFetching,
    itemsId,
    onAddItem,
    onFetchEntriesNextPage,
    entries,
    inAssortmentProducts,
    collectionEditable,
    collection,
    shopViewFields,
    brandEntities,
}) => {
    const parsedFilters = React.useMemo(() => parseFilters(router.location), [router.location]);
    const [filters, setFilters] = React.useState<Map<string, List<string>>>(parsedFilters);
    const [filtersOpen, setFiltersOpen] = React.useState<HTMLButtonElement>(undefined);
    const breadcrumbsRef = React.useRef<HTMLDivElement>();
    const allowDuplicateProduct = collection?.getIn(
        CollectionKeys.ITEM_DISPLAY_PROPERTY.ALLOW_DUPLICATE_PRODUCT_ENTRIES
    );
    const itemDisplayProperty = collection?.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS);

    const muiStyles = React.useMemo(() => {
        return {
            circularProgress: {
                width: "100px",
                margin: "0px auto",
            },
            popover: {
                width: "280px",
                height: "500px",
            },
        };
    }, []);

    React.useEffect(() => {
        onFetchCatalog(router.params.catalog);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        const _filters = parseFilters(router.location);
        setFilters(_filters);
    }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    const viewDefRecord = buildViewRecord(router.params.brand);

    const handleOpenFilters = React.useCallback(({ currentTarget }) => {
        setFiltersOpen(currentTarget);
    }, []);

    const handleCloseFilters = React.useCallback(() => {
        setFiltersOpen(undefined);
    }, []);

    const handleFiltersChange = React.useCallback(
        (_filters) => {
            const query = { ...router.location.query };
            delete query.brand;

            if (_filters.size > 0) {
                try {
                    query.filters = JSON.stringify(_filters.toJS());
                } catch (e) {
                    // no-op
                }
            } else {
                delete query.filters;
            }
            router.replace({
                pathname: router.location.pathname,
                query: {
                    ...query,
                    mode: query.mode,
                },
            });
        },
        [router]
    );
    const rawItemDisplayProperty: ISchemaKey[] = getRawItemDisplayProperty(itemDisplayProperty);

    return (
        <React.Fragment>
            <div className="shopHeader">
                {children}
                <IconButton onClick={handleOpenFilters}>
                    <RefineIcon color={theme.palette.darkGrey} hoverColor={theme.palette.darkerGrey} />
                </IconButton>
                <Popover
                    open={!!filtersOpen}
                    anchorEl={filtersOpen}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    targetOrigin={{ horizontal: "right", vertical: "top" }}
                    onRequestClose={handleCloseFilters}
                    style={muiStyles.popover}
                >
                    <Filters
                        resource="item"
                        activeFiltersValues={filters}
                        onFilterChange={handleFiltersChange}
                        hiddenFilters={["__inventory"]}
                        catalog={catalog}
                    />
                </Popover>
            </div>
            <Breadcrumbs breadcrumbs={breadcrumbs} breadcrumbsRef={breadcrumbsRef} />
            <ProductCollectionContext.Consumer>
                {({ shopViewSettings }) => (
                    <ProductCollectionCatalog
                        brandEntities={brandEntities}
                        catalog={catalog}
                        app={app}
                        entities={entities}
                        entries={entries}
                        inAssortmentProducts={inAssortmentProducts}
                        isViewDefFetching={isViewDefFetching}
                        itemsId={itemsId}
                        items={items}
                        location={location}
                        onAddItem={onAddItem}
                        onFetchEntries={onFetchEntries}
                        onFetchEntriesNextPage={onFetchEntriesNextPage}
                        scope={scope}
                        sidePanelWidth={0}
                        viewDef={viewDefRecord}
                        // @ts-ignore view record is .js
                        viewDefEntities={{ [viewDefRecord._id]: viewDefRecord }}
                        // @ts-ignore view record is .js
                        viewDefIds={[viewDefRecord._id]}
                        collectionEditable={collectionEditable}
                        router={router}
                        filters={filters}
                        isSidePanel={true}
                        overrideStyles={{
                            gridWidth: 320,
                            headerHeight:
                                HEADER_HEIGHT + (breadcrumbsRef.current ? breadcrumbsRef.current.offsetHeight : 0),
                        }}
                        allowDuplicateProduct={allowDuplicateProduct}
                        shopViewFields={shopViewFields}
                        itemDisplayProperty={itemDisplayProperty}
                        shopViewSettings={shopViewSettings}
                        rawItemDisplayProperty={rawItemDisplayProperty}
                    />
                )}
            </ProductCollectionContext.Consumer>
        </React.Fragment>
    );
};
